/**
 * Nono Cookie Banner + Preferences Modal
 * --------------------------------------
 * Custom UI replacement for Shopify's default Customer Privacy banner.
 *
 *   ⚠️ FUTURE DEVS — READ THIS BEFORE TOUCHING ⚠️
 *
 * This is a UI-only replacement. The legal/compliance backend is still
 * Shopify's Customer Privacy API — we just hide their UI and call their API
 * from our buttons. This means:
 *
 *   1. Removing this code does NOT disable consent collection. Shopify's
 *      default banner will simply reappear (it's hidden, not deleted).
 *
 *   2. Consent state lives in Shopify's `_tracking_consent` cookie + their
 *      audit log. The preferred path is the API:
 *      `Shopify.customerPrivacy.setTrackingConsent({analytics, marketing,
 *      preferences, sale_of_data}, callback)`. BUT — on this theme as of
 *      2026-04-27, `Shopify.customerPrivacy` is NOT auto-loaded. It only
 *      becomes available after explicitly calling
 *      `Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.1'}])`.
 *      Even then, the load can be slow or silently fail.
 *
 *      Solution: writeShopifyFormatCookie() pre-writes the cookie in
 *      Shopify's nested v2.1 format BEFORE the API call. Shopify's banner
 *      logic checks this cookie's `display_banner: false` field on each
 *      page load — if the cookie is in their format, banner stays gone.
 *
 *      The flat fallback format (`{analytics: 'yes', ...}`) does NOT work —
 *      Shopify ignores it and re-shows the banner. Don't change
 *      writeShopifyFormatCookie() to a flatter format without testing
 *      cross-page persistence after a click.
 *
 *   3. Region targeting is delegated to Shopify. We piggyback on
 *      `shouldShowGDPRBanner()` indirectly: if Shopify rendered its banner
 *      in the DOM, we mirror that decision and show ours. This is why
 *      watchForShopifyBanner() exists — it's not just a hide-helper, it's
 *      our trigger for visibility too.
 *
 *   4. Selectors targeting Shopify's banner ([id^="shopify-pc__"] etc.)
 *      are tightly coupled to Shopify's current DOM. If Shopify migrates
 *      to a Web Component / Shadow DOM (they've hinted at this), update
 *      both the CSS rules in cookie-banner.css AND the JS killShopify-
 *      DefaultBanner() selector list.
 *
 *   5. The MutationObserver runs on document.documentElement subtree.
 *      Cheap callback (one querySelectorAll), but keep an eye on perf
 *      if heavy DOM-mutating apps are added.
 *
 *   6. Skipped paths: /checkouts/, /checkout, /account/. These are
 *      Shopify-owned surfaces — do NOT add our banner there.
 *
 *   7. To re-open the modal from anywhere on the site, add a link with
 *      `data-cookie-prefs="true"` — the delegated handler at the bottom
 *      of init() picks it up. Currently used in the footer text block
 *      (see sections/footer-group.json: text_zmC37i).
 *
 * Files involved (5):
 *   - assets/cookie-banner.css         — visual styling
 *   - assets/cookie-banner.js          — this file
 *   - snippets/cookie-banner.liquid    — banner + modal markup
 *   - layout/theme.liquid              — CSS link + snippet render + JS load
 *   - sections/footer-group.json       — footer "Cookie-innstillinger" link
 *
 * Public API:
 *   - window.NonoCookieBanner.show()             — show banner
 *   - window.NonoCookieBanner.openPreferences()  — open prefs modal
 *
 * History:
 *   - 2026-04-27 — Initial ship. Replaces Shopify default banner that was
 *     blocking 60-70% of mobile viewport, killing /pages/provetime
 *     conversion. Built from Claude Design system handoff (v3 + v4).
 *     SEO action plan: C4.
 *   - 2026-04-27 — Hotfix 1: MutationObserver was re-showing the banner
 *     after consent click because Shopify mutates its own (hidden)
 *     shopify-pc__ elements when recording consent. Added consentRecorded
 *     flag + observer disconnect.
 *   - 2026-04-27 — Hotfix 2: Banner kept reappearing on cross-page
 *     navigation because Shopify.customerPrivacy was never auto-loaded on
 *     this theme — my polling timed out and a flat fallback cookie was
 *     written, which Shopify ignored. Fix: pre-write cookie in Shopify's
 *     nested v2.1 format via writeShopifyFormatCookie() so consent persists
 *     regardless of API state. Verified live: cross-page persistence works,
 *     cookie format is Shopify-recognized.
 */
(function () {
  'use strict';

  // ------------------------------------------------------------------------
  // Skip on Shopify-managed pages
  // ------------------------------------------------------------------------
  var path = window.location.pathname || '';
  if (/^\/(checkouts|account)\b/.test(path) || /^\/checkout/.test(path)) {
    return;
  }

  // ------------------------------------------------------------------------
  // Config
  // ------------------------------------------------------------------------
  var BANNER_ID = 'nono-cookie-banner';
  var MODAL_ID  = 'nono-cookie-modal';
  var POLL_INTERVAL = 100;
  var POLL_TIMEOUT  = 8000;

  // ------------------------------------------------------------------------
  // State
  // ------------------------------------------------------------------------
  var banner = null;
  var modal = null;
  var primaryBtn = null;
  var secondaryBtn = null;
  var settingsLink = null;
  var modalSaveBtn = null;
  var modalEssentialBtn = null;
  var modalAcceptAllBtn = null;
  var modalCloseEls = null;
  var analyticsToggle = null;
  var marketingToggle = null;
  var lastFocusedBeforeModal = null;
  var consentRecorded = false; // flips true after any consent button click
  var bannerObserver = null;

  // ------------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------------
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function getApi() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (cp && typeof cp.setTrackingConsent === 'function') return cp;
    return null;
  }

  // Shopify moved customerPrivacy behind loadFeatures(['consent-tracking-api']).
  // Themes must explicitly request it — it doesn't auto-load.
  // Once requested, window.Shopify.customerPrivacy.setTrackingConsent appears.
  function requestApiLoad() {
    if (getApi()) return; // already loaded
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      try {
        window.Shopify.loadFeatures([
          { name: 'consent-tracking-api', version: '0.1' }
        ], function (error) {
          if (error) {
            // Silent fail — fallback paths still work
          }
        });
      } catch (e) { /* ignore */ }
    }
  }

  function pollForApi(callback) {
    // Trigger feature load up front
    requestApiLoad();
    var elapsed = 0;
    var timer = setInterval(function () {
      var api = getApi();
      if (api) {
        clearInterval(timer);
        callback(api);
        return;
      }
      elapsed += POLL_INTERVAL;
      if (elapsed >= POLL_TIMEOUT) clearInterval(timer);
    }, POLL_INTERVAL);
  }

  function consentDecided(api) {
    try {
      var consent = typeof api.currentVisitorConsent === 'function'
        ? api.currentVisitorConsent()
        : null;
      if (consent && (consent.analytics === 'yes' || consent.analytics === 'no')) {
        return true;
      }
    } catch (e) { /* ignore */ }
    return false;
  }

  // ------------------------------------------------------------------------
  // Banner show/hide
  // ------------------------------------------------------------------------
  function showBanner() {
    if (!banner) return;
    requestAnimationFrame(function () {
      banner.setAttribute('data-visible', 'true');
    });
  }

  function hideBanner() {
    if (!banner) return;
    banner.setAttribute('data-visible', 'false');
  }

  // ------------------------------------------------------------------------
  // Modal show/hide
  // ------------------------------------------------------------------------
  function syncTogglesFromConsent() {
    var api = getApi();
    var consent = null;
    if (api && typeof api.currentVisitorConsent === 'function') {
      try { consent = api.currentVisitorConsent(); } catch (e) {}
    }
    var analyticsOn = consent && consent.analytics === 'yes';
    var marketingOn = consent && consent.marketing === 'yes';
    setToggle(analyticsToggle, analyticsOn);
    setToggle(marketingToggle, marketingOn);
  }

  function setToggle(toggle, on) {
    if (!toggle) return;
    toggle.setAttribute('aria-checked', on ? 'true' : 'false');
  }

  function getToggle(toggle) {
    if (!toggle) return false;
    return toggle.getAttribute('aria-checked') === 'true';
  }

  function openPreferences() {
    if (!modal) return;
    lastFocusedBeforeModal = document.activeElement;
    syncTogglesFromConsent();
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('nono-cookie-modal-open');
    setTimeout(function () {
      var closeBtn = modal.querySelector('[data-modal-close]');
      if (closeBtn) closeBtn.focus();
    }, 50);
  }

  function closePreferences() {
    if (!modal) return;
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('nono-cookie-modal-open');
    // Return focus where it came from
    if (lastFocusedBeforeModal && typeof lastFocusedBeforeModal.focus === 'function') {
      try { lastFocusedBeforeModal.focus(); } catch (e) {}
    }
    lastFocusedBeforeModal = null;
  }

  // ------------------------------------------------------------------------
  // Apply consent (all 3 paths route through here)
  // ------------------------------------------------------------------------
  // Write Shopify's actual nested _tracking_consent format directly.
  // Used as last-resort fallback when the API can't be reached.
  // Format derived from Shopify's customer-privacy.js v2.1.
  function writeShopifyFormatCookie(consent) {
    var format = {
      v: '2.1',
      con: {
        CMP: {
          a: consent.analytics ? '1' : '0',
          m: consent.marketing ? '1' : '0',
          p: consent.preferences ? '1' : '0',
          s: '0'
        }
      },
      region: (window.Shopify && window.Shopify.country) || 'NO',
      reg: '["GDPR"]',
      purposes: {
        a: !!consent.analytics,
        m: !!consent.marketing,
        p: !!consent.preferences,
        t: false
      },
      display_banner: false,
      sale_of_data_region: false
    };
    try {
      var encoded = encodeURIComponent(JSON.stringify(format));
      var oneYear = 60 * 60 * 24 * 365;
      document.cookie = '_tracking_consent=' + encoded
        + '; max-age=' + oneYear + '; path=/; SameSite=Lax';
    } catch (e) { /* ignore */ }
  }

  function applyConsent(consentObj) {
    // Mark consent recorded so the MutationObserver stops re-showing the banner
    // when Shopify mutates its own (now-hidden) shopify-pc__ elements to
    // record the consent state.
    consentRecorded = true;
    if (bannerObserver) {
      try { bannerObserver.disconnect(); } catch (e) {}
      bannerObserver = null;
    }

    // Always close UI immediately — never wait on API
    hideBanner();
    closePreferences();

    var consent = {
      analytics:    !!consentObj.analytics,
      marketing:    !!consentObj.marketing,
      preferences:  !!consentObj.preferences,
      sale_of_data: 'no'
    };

    // Always pre-write the Shopify-format cookie so consent is persisted
    // across page loads even if the API never resolves. If the API DOES
    // resolve, Shopify overwrites our cookie with its own (which is fine).
    writeShopifyFormatCookie(consent);

    // Try to call Shopify's official API so the consent is also recorded
    // in their audit log and used by their tracking pixels.
    function callApi(api) {
      try {
        api.setTrackingConsent(consent, function () { /* recorded */ });
      } catch (e) { /* swallow — cookie already written */ }
    }

    // Path A: API already on the global
    var api = getApi();
    if (api) { callApi(api); return; }

    // Path B: load the feature, then call API in the callback
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      var done = false;
      var timeoutTimer = setTimeout(function () {
        if (!done) { done = true; /* fallback cookie already written */ }
      }, 15000);
      try {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function (error) {
            if (done) return;
            done = true;
            clearTimeout(timeoutTimer);
            if (error) return; // fallback cookie already in place
            var loadedApi = getApi();
            if (loadedApi) callApi(loadedApi);
          }
        );
      } catch (e) {
        clearTimeout(timeoutTimer);
        // fallback cookie already in place
      }
    }
    // else: no loadFeatures available — fallback cookie is all we have
  }

  // ------------------------------------------------------------------------
  // Wiring
  // ------------------------------------------------------------------------
  function wireBanner() {
    if (primaryBtn) {
      primaryBtn.addEventListener('click', function () {
        applyConsent({ analytics: true, marketing: true, preferences: true });
      });
    }
    if (secondaryBtn) {
      secondaryBtn.addEventListener('click', function () {
        applyConsent({ analytics: false, marketing: false, preferences: false });
      });
    }
    if (settingsLink) {
      settingsLink.addEventListener('click', function () {
        openPreferences();
      });
    }
  }

  function wireModal() {
    // Toggle clicks
    if (analyticsToggle) {
      analyticsToggle.addEventListener('click', function () {
        setToggle(analyticsToggle, !getToggle(analyticsToggle));
      });
    }
    if (marketingToggle) {
      marketingToggle.addEventListener('click', function () {
        setToggle(marketingToggle, !getToggle(marketingToggle));
      });
    }
    // Save
    if (modalSaveBtn) {
      modalSaveBtn.addEventListener('click', function () {
        var analytics = getToggle(analyticsToggle);
        var marketing = getToggle(marketingToggle);
        applyConsent({
          analytics:   analytics,
          marketing:   marketing,
          preferences: analytics // bundle preferences with analytics
        });
      });
    }
    // Bare nødvendige inside modal
    if (modalEssentialBtn) {
      modalEssentialBtn.addEventListener('click', function () {
        applyConsent({ analytics: false, marketing: false, preferences: false });
      });
    }
    // Godta alle inside modal
    if (modalAcceptAllBtn) {
      modalAcceptAllBtn.addEventListener('click', function () {
        applyConsent({ analytics: true, marketing: true, preferences: true });
      });
    }
    // Close (X + backdrop)
    if (modalCloseEls) {
      for (var i = 0; i < modalCloseEls.length; i++) {
        modalCloseEls[i].addEventListener('click', closePreferences);
      }
    }
    // ESC
    document.addEventListener('keydown', function (e) {
      if (modal && modal.getAttribute('aria-hidden') === 'false' && e.key === 'Escape') {
        closePreferences();
      }
    });
  }

  // ------------------------------------------------------------------------
  // Belt + suspenders: hide Shopify default banner; show ours when detected
  // ------------------------------------------------------------------------
  function killShopifyDefaultBanner() {
    var nodes = document.querySelectorAll(
      '[id^="shopify-pc__"], [class^="shopify-pc__"], [class*=" shopify-pc__"]'
    );
    if (nodes.length === 0) return false;
    for (var i = 0; i < nodes.length; i++) {
      try {
        nodes[i].style.setProperty('display', 'none', 'important');
        nodes[i].style.setProperty('visibility', 'hidden', 'important');
      } catch (e) {}
    }
    return true;
  }

  function watchForShopifyBanner() {
    function killAndShow() {
      var found = killShopifyDefaultBanner();
      if (!found) return;
      // Don't re-show our banner after the user has already decided.
      // (Shopify mutates its own shopify-pc__ elements to record the consent
      // state, which would otherwise re-trigger this callback.)
      if (consentRecorded) return;
      var api = getApi();
      if (api && consentDecided(api)) return;
      showBanner();
    }
    if ('MutationObserver' in window) {
      bannerObserver = new MutationObserver(killAndShow);
      bannerObserver.observe(document.documentElement, { childList: true, subtree: true });
    }
    killAndShow();
    setTimeout(killAndShow, 250);
    setTimeout(killAndShow, 1000);
    setTimeout(killAndShow, 2500);
  }

  // ------------------------------------------------------------------------
  // Public API (for footer link to re-open preferences)
  // ------------------------------------------------------------------------
  window.NonoCookieBanner = {
    show:             function () { showBanner(); },
    openPreferences:  function () { openPreferences(); }
  };

  // ------------------------------------------------------------------------
  // Init
  // ------------------------------------------------------------------------
  ready(function () {
    // Trigger Shopify's customer-privacy feature load as early as possible
    // so the API is ready by the time the user clicks a consent button.
    requestApiLoad();

    watchForShopifyBanner();

    banner = document.getElementById(BANNER_ID);
    modal  = document.getElementById(MODAL_ID);
    if (!banner) return;

    primaryBtn        = banner.querySelector('[data-action="accept"]');
    secondaryBtn      = banner.querySelector('[data-action="essential"]');
    settingsLink      = banner.querySelector('[data-action="settings"]');

    if (modal) {
      modalSaveBtn       = modal.querySelector('[data-action="save"]');
      modalEssentialBtn  = modal.querySelector('[data-action="essential-modal"]');
      modalAcceptAllBtn  = modal.querySelector('[data-action="accept-all-modal"]');
      modalCloseEls      = modal.querySelectorAll('[data-modal-close]');
      analyticsToggle    = modal.querySelector('[data-toggle="analytics"]');
      marketingToggle    = modal.querySelector('[data-toggle="marketing"]');
    }

    wireBanner();
    if (modal) wireModal();

    // Delegated handler: any [data-cookie-prefs] link anywhere on the page
    // (e.g. footer "Cookie-innstillinger") opens the preferences modal.
    document.addEventListener('click', function (e) {
      var target = e.target && e.target.closest && e.target.closest('[data-cookie-prefs]');
      if (target) {
        e.preventDefault();
        openPreferences();
      }
    });

    // If API loads and consent already decided, suppress banner
    pollForApi(function (api) {
      if (consentDecided(api)) hideBanner();
    });
  });
})();

/* nono-booking SELF-HEAL (2026-06-10 incident): Shopify kept serving a fossil
 * render of /pages/provetime (deleted page id, old theme assets) in which the
 * booking modal + controller never rendered — dead booking buttons on a page
 * fed by live ads. This bootstrap rides the one asset the fossil still loads:
 * if a page has [data-booking-trigger] but no #lp-booking-modal, it injects the
 * modal (markup + styles, mirroring snippets/booking-modal.liquid) and loads
 * nono-booking.js from the same asset directory. No-op on healthy pages. */
(function(){
  "use strict";
  var MODAL_HTML="<div\n  id=\"lp-booking-modal\"\n  class=\"lp-booking-modal\"\n  role=\"dialog\"\n  aria-modal=\"true\"\n  aria-labelledby=\"lp-booking-modal-title\"\n  data-booking-iframe-url=\"https://momence.com/Nono/appointment-reservation/132069?boardId=114451\"\n  data-attribution-endpoint=\"https://nono-mirror-production.up.railway.app\"\n  data-support-phone=\"+4740600235\"\n  \n  hidden\n>\n  <div class=\"lp-booking-modal__backdrop\" data-booking-close></div>\n  <div class=\"lp-booking-modal__panel\" role=\"document\">\n    <header class=\"lp-booking-modal__head\">\n      <div class=\"lp-booking-modal__head-title\">\n        <span class=\"lp-booking-modal__dot\" aria-hidden=\"true\"></span>\n        <h2 id=\"lp-booking-modal-title\">Book gratis prøvetime</h2>\n      </div>\n      <button type=\"button\" class=\"lp-booking-modal__close\" data-booking-close aria-label=\"Lukk\">\n        <svg viewBox=\"0 0 24 24\" width=\"22\" height=\"22\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2.5\" stroke-linecap=\"round\" stroke-linejoin=\"round\" aria-hidden=\"true\"><line x1=\"18\" y1=\"6\" x2=\"6\" y2=\"18\"/><line x1=\"6\" y1=\"6\" x2=\"18\" y2=\"18\"/></svg>\n      </button>\n    </header>\n\n    <div class=\"lp-booking-modal__body\">\n      <div class=\"lp-booking-modal__skeleton\" data-booking-skeleton>\n        <span class=\"lp-booking-modal__spinner\" aria-hidden=\"true\"></span>\n        <p>Laster bookingkalenderen …</p>\n        <span class=\"visually-hidden\" role=\"status\">Laster bookingkalender</span>\n      </div>\n      <div class=\"lp-booking-modal__iframe-host\" data-booking-iframe-host></div>\n      <div class=\"lp-booking-modal__success\" data-booking-success role=\"status\" aria-live=\"assertive\" tabindex=\"-1\">\n        <span class=\"lp-booking-modal__success-check\" aria-hidden=\"true\">\n          <svg viewBox=\"0 0 24 24\" width=\"34\" height=\"34\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"3\" stroke-linecap=\"round\" stroke-linejoin=\"round\"><path d=\"M20 6 9 17l-5-5\"/></svg>\n        </span>\n        <h3>Booket!</h3>\n        <p>Takk for at du booket prøvetime hos Nono Moss. Du får en bekreftelse på e-post nå. Vi gleder oss til å se deg.</p>\n        <button type=\"button\" class=\"lp-booking-modal__success-back\" data-booking-success-dismiss>Tilbake til kalenderen</button>\n      </div>\n    </div>\n\n    <footer class=\"lp-booking-modal__foot\">\n      <svg viewBox=\"0 0 24 24\" width=\"16\" height=\"16\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2.5\" stroke-linecap=\"round\" stroke-linejoin=\"round\" aria-hidden=\"true\"><path d=\"M20 6 9 17l-5-5\"/></svg>\n      <p>Vi bekrefter bookingen din på e-post umiddelbart.</p>\n    </footer>\n  </div>\n</div>\n\n<style>\n  .lp-booking-modal {\n    position: fixed;\n    inset: 0;\n    z-index: 1000;\n    display: grid;\n    place-items: center;\n  }\n  .lp-booking-modal[hidden] { display: none; }\n\n  .lp-booking-modal__backdrop {\n    position: absolute;\n    inset: 0;\n    background: rgba(26, 6, 15, 0.6);\n    backdrop-filter: blur(3px);\n    cursor: pointer;\n    animation: lpBkFade 0.2s ease;\n  }\n  @keyframes lpBkFade { from { opacity: 0; } to { opacity: 1; } }\n\n  .lp-booking-modal__panel {\n    position: relative;\n    width: min(960px, 96vw);\n    height: min(880px, 92vh);\n    background: #fff;\n    border-radius: 20px;\n    display: flex;\n    flex-direction: column;\n    overflow: hidden;\n    box-shadow: 0 40px 90px rgba(0, 0, 0, 0.4);\n    animation: lpBkRise 0.25s cubic-bezier(0.16, 1, 0.3, 1);\n  }\n  @keyframes lpBkRise {\n    from { opacity: 0; transform: translateY(16px) scale(0.98); }\n    to { opacity: 1; transform: translateY(0) scale(1); }\n  }\n  @media (prefers-reduced-motion: reduce) {\n    .lp-booking-modal__backdrop,\n    .lp-booking-modal__panel { animation: none; }\n  }\n\n  .lp-booking-modal__head {\n    display: flex;\n    align-items: center;\n    justify-content: space-between;\n    gap: 1rem;\n    padding: 0.9rem 1.25rem;\n    border-bottom: 1px solid rgba(88, 11, 38, 0.1);\n    background: linear-gradient(180deg, #fff 0%, #fef6ec 100%);\n    flex: 0 0 auto;\n  }\n  .lp-booking-modal__head-title {\n    display: flex;\n    align-items: center;\n    gap: 0.6rem;\n    min-width: 0;\n  }\n  .lp-booking-modal__dot {\n    width: 12px;\n    height: 12px;\n    border-radius: 50%;\n    background: linear-gradient(145deg, #580B26 6.58%, #B71F60 94.27%);\n    flex: 0 0 auto;\n  }\n  .lp-booking-modal__head h2 {\n    margin: 0;\n    color: #1a1a1a;\n    font-size: 1.1rem;\n    font-weight: 700;\n    white-space: nowrap;\n    overflow: hidden;\n    text-overflow: ellipsis;\n  }\n  .lp-booking-modal__close {\n    display: inline-grid;\n    place-items: center;\n    width: 40px;\n    height: 40px;\n    border-radius: 50%;\n    background: rgba(88, 11, 38, 0.08);\n    color: #580B26;\n    border: 0;\n    cursor: pointer;\n    flex: 0 0 auto;\n    min-block-size: 40px;\n    min-inline-size: 40px;\n    transition: background 0.15s ease;\n  }\n  .lp-booking-modal__close:hover,\n  .lp-booking-modal__close:focus-visible { background: rgba(88, 11, 38, 0.16); }\n\n  .lp-booking-modal__body {\n    position: relative;\n    flex: 1 1 auto;\n    min-height: 0;\n    background: #fdf6ef;\n  }\n  .lp-booking-modal__iframe-host {\n    position: absolute;\n    inset: 0;\n  }\n  .lp-booking-modal__iframe-host iframe {\n    width: 100%;\n    height: 100%;\n    border: 0;\n    display: block;\n  }\n\n  .lp-booking-modal__skeleton {\n    position: absolute;\n    inset: 0;\n    display: flex;\n    flex-direction: column;\n    align-items: center;\n    justify-content: center;\n    gap: 1rem;\n    background: #fdf6ef;\n    color: #580B26;\n    z-index: 1;\n    transition: opacity 0.25s ease;\n  }\n  .lp-booking-modal__skeleton[hidden] { display: none; }\n  .lp-booking-modal__skeleton.is-fading { opacity: 0; }\n  .lp-booking-modal__skeleton p {\n    margin: 0;\n    font-size: var(--font-sm, 0.95rem);\n    color: #8a5468;\n  }\n  .lp-booking-modal__spinner {\n    width: 42px;\n    height: 42px;\n    border-radius: 50%;\n    border: 4px solid rgba(88, 11, 38, 0.15);\n    border-top-color: #B71F60;\n    animation: lpBkSpin 0.8s linear infinite;\n  }\n  @keyframes lpBkSpin { to { transform: rotate(360deg); } }\n  @media (prefers-reduced-motion: reduce) {\n    .lp-booking-modal__spinner { animation-duration: 2s; }\n  }\n\n  .lp-booking-modal__foot {\n    display: flex;\n    align-items: center;\n    justify-content: center;\n    gap: 0.5rem;\n    padding: 0.75rem 1.25rem;\n    background: #fff;\n    border-top: 1px solid rgba(88, 11, 38, 0.1);\n    color: #2d8a4e;\n    flex: 0 0 auto;\n  }\n  .lp-booking-modal__foot svg { flex: 0 0 auto; }\n  .lp-booking-modal__foot p {\n    margin: 0;\n    font-size: var(--font-sm, 0.9rem);\n    color: #555;\n  }\n\n  .lp-booking-modal__success {\n    position: absolute;\n    inset: 0;\n    /* Above the escape overlay (z-index 4) so a genuine confirmation can never\n       be buried by a late stall/escape screen. */\n    z-index: 5;\n    display: none;\n    flex-direction: column;\n    align-items: center;\n    justify-content: center;\n    gap: 0.75rem;\n    padding: 2rem;\n    text-align: center;\n    background: linear-gradient(160deg, #fef6ec 0%, #fdeee6 100%);\n  }\n  .lp-booking-modal__success.is-visible { display: flex; }\n  .lp-booking-modal__success-back {\n    margin-top: 0.5rem;\n    background: none;\n    border: 0;\n    color: #580B26;\n    font-weight: 600;\n    font-size: var(--font-sm, 0.9rem);\n    text-decoration: underline;\n    text-underline-offset: 3px;\n    cursor: pointer;\n    min-height: 44px;\n    padding: 0 12px;\n  }\n  .lp-booking-modal__success-check {\n    width: 64px;\n    height: 64px;\n    border-radius: 50%;\n    display: grid;\n    place-items: center;\n    background: #2d8a4e;\n    color: #fff;\n    margin-block-end: 0.5rem;\n  }\n  .lp-booking-modal__success h3 {\n    margin: 0;\n    font-size: clamp(1.5rem, 4vw, 2rem);\n    color: #1a1a1a;\n    font-weight: 800;\n  }\n  .lp-booking-modal__success p {\n    margin: 0;\n    color: #444;\n    font-size: var(--font-md, 1.05rem);\n    max-width: 420px;\n  }\n\n  @media (max-width: 600px) {\n    .lp-booking-modal__panel {\n      width: 100vw;\n      height: 100dvh;\n      max-height: 100dvh;\n      border-radius: 0;\n    }\n  }\n</style>";
  function heal(){
    try{
      if(document.getElementById("lp-booking-modal"))return;
      if(!document.querySelector("[data-booking-trigger]"))return;
      // Own-booking pages ship their OWN modal + controller (lp-own-booking
      // section / nono-own-booking.js). Do NOT inject the legacy Momence modal
      // there, or both would open from the same [data-booking-trigger].
      if(document.querySelector("[data-nono-booking]"))return;
      document.body.insertAdjacentHTML("beforeend",MODAL_HTML);
      var me=document.querySelector("script[src*=\"cookie-banner.js\"]");
      if(!me||!me.src)return;
      var s=document.createElement("script");
      s.src=me.src.replace(/cookie-banner\.js[^\"]*$/,"nono-booking.js?heal=20260610");
      s.defer=true;
      document.head.appendChild(s);
    }catch(e){ /* never break the host page */ }
  }
  if(document.readyState==="loading")document.addEventListener("DOMContentLoaded",heal);
  else heal();
})();
